import {partition} from 'lodash';
import React, {useMemo, useRef} from 'react';
import styled from 'styled-components';

import {Handler, HandlerOf} from '../../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {AsyncStatusesEnum} from '../../../../../../../libs/shared/helpers/src/types/asyncStatuses';
import {Dropdown} from '../../../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownItemCheckbox} from '../../../../../../../libs/shared/ui/src/components/dropdowns/dropdownItemCheckbox';
import {DropdownList} from '../../../../../../../libs/shared/ui/src/components/dropdowns/dropdownList';
import {PopoverTransferProps} from '../../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {useFetchTagList} from '../../../../../../helpers/safetyNetsHelpers';
import {ConversationBaseModel} from '../../../../../../models/api/conversationModel';
import {SafetyNetRequiredTaggingModel} from '../../../../../../models/api/safetyNetModel';
import {TagModel, TagPathModel} from '../../../../../../models/api/tagModel';
import {DropdownEmpty} from '../../../../../common/dropdowns/dropdownEmpty';
import {DropdownLoader} from '../../../../../common/dropdowns/dropdownLoader';
import {TagDropdownItem} from '../../../../../common/dropdowns/tagDropdownItem';

/*
 * Props.
 */

interface RequiredTaggingDropdownProps extends PopoverTransferProps {
  requiredTaggingSafetyNet: SafetyNetRequiredTaggingModel;
  conversations: ReadonlyArray<ConversationBaseModel>;
  onTagConversation: HandlerOf<TagModel>;
  onUntagConversation: HandlerOf<TagModel>;
}

/*
 * Styles.
 */

const StyledDropdownList = styled(DropdownList)`
  max-height: calc(100vh - 20px);
`;

/*
 * Component.
 */

export const RequiredTaggingDropdown: React.FC<RequiredTaggingDropdownProps> = (props) => {
  const {requiredTaggingSafetyNet, conversations} = props;

  const {tagsPath, status, onRetry} = useFetchTagList(requiredTaggingSafetyNet.dependencyIds);

  // Show initially selected tags first.
  const initialSelectedIdsMemo = useMemo(
    () =>
      requiredTaggingSafetyNet.dependencyIds.filter((tagId) =>
        conversations.every((conversation) => conversation.tagIds.includes(tagId)),
      ),
    [conversations, requiredTaggingSafetyNet.dependencyIds],
  );
  const initialSelectedIds = useRef(initialSelectedIdsMemo);
  const orderedTagsPath = useMemo(() => {
    if (!tagsPath) {
      return undefined;
    }

    const [selectedTags, remainingTags] = partition(tagsPath, ([tag]) =>
      initialSelectedIds.current.includes(tag.id),
    );

    return [...selectedTags, ...remainingTags];
  }, [tagsPath]);

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <Dropdown {...props} placement="left-start" shouldSkipApplyMaxSize width={260}>
      <StyledDropdownList itemCount={tagsPath?.length}>
        {renderContent(orderedTagsPath, status, onRetry, props)}
      </StyledDropdownList>
    </Dropdown>
  );
};

/*
 * Helpers.
 */

function renderContent(
  tagsPath: ReadonlyArray<TagPathModel> | undefined,
  status: AsyncStatusesEnum,
  onRetry: Handler,
  props: RequiredTaggingDropdownProps,
) {
  const {conversations, onTagConversation, onUntagConversation, requiredTaggingSafetyNet} = props;

  if (tagsPath === undefined) {
    return <DropdownLoader status={status} onRetryClick={onRetry} />;
  }

  if (tagsPath.length === 0) {
    return <DropdownEmpty />;
  }

  return tagsPath.map((tagPath) => (
    <TagItem
      key={tagPath[0].id}
      tagPath={tagPath}
      conversations={conversations}
      onTagConversation={onTagConversation}
      onUntagConversation={onUntagConversation}
      isOneTagRequired={requiredTaggingSafetyNet.isOneTagRequired}
    />
  ));
}

/*
 * Props.
 */

interface TagItemProps {
  tagPath: TagPathModel;
  conversations: ReadonlyArray<ConversationBaseModel>;
  onTagConversation: HandlerOf<TagModel>;
  onUntagConversation: HandlerOf<TagModel>;
  isOneTagRequired: boolean;
}

/*
 * Component.
 */

const TagItem: React.FC<TagItemProps> = (props) => {
  const {tagPath, conversations, onTagConversation, onUntagConversation, isOneTagRequired} = props;

  const [tag] = tagPath;

  const isChecked = useMemo(
    () => conversations.every((c) => c.tagIds.includes(tag.id)),
    [conversations, tag],
  );

  return (
    <TagDropdownItem
      tag={tag}
      nestingLevel={0}
      isChecked={isChecked}
      onSelect={() => (isChecked ? onUntagConversation(tag) : onTagConversation(tag))}
      renderRightContent={
        !isOneTagRequired ? () => <DropdownItemCheckbox isChecked={isChecked} /> : undefined
      }
      tagsPath={tagPath || []}
    />
  );
};
